import type { ExtractedSourceDocument, GenerateDefinitionRequest, GeneratedDefinition, OrganisationAllowance } from "@keydials/api-client";
import { createDraft, type BuilderLoadDeps, type CreateResult } from "./builderLoad";
import { builderCopy as copy, fill } from "./copy";
import type { NounForms } from "./draftSession";

// ---------------------------------------------------------------------------
// Describe it (KEY-560): the author writes a paragraph about their process and the AI drafts the dial. A draft costs one of the
// month's AI drafts the moment it starts, and creating the dial costs a design slot, so the plan is checked before either is
// spent. The request carries only what the author wrote: never a model or a group, which the server refuses an author anyway.
// A document the author adds is read into text first and goes with the description; the file itself is never kept.
// ---------------------------------------------------------------------------

export interface DescribeDeps extends BuilderLoadDeps {
  /** Drafts a dial. Aborting stops waiting for it; the job itself carries on and is still charged. */
  generate(request: GenerateDefinitionRequest, signal: AbortSignal): Promise<GeneratedDefinition>;
  /** Reads a document's text so it can go with the description. */
  extractDocument(file: File): Promise<ExtractedSourceDocument>;
}

/** What the author gave at Start for a described dial. The name and purpose are optional; the draft supplies its own. */
export interface DescribeChoices {
  description: string;
  name: string;
  purpose: string;
  kind: "Simple" | "Itemised";
  noun: NounForms | null;
  /** A document the author added, already read into text, or null. */
  document: ExtractedSourceDocument | null;
}

/** Where the month's AI drafts stand, for the Describe it card. */
export type AiMeter =
  | { state: "unmetered" }
  | { state: "locked" }
  | { state: "left"; left: number; resetsOn: string | null }
  | { state: "usedUp"; allowed: number; resetsOn: string | null };

export type DescribeResult =
  | CreateResult
  | { kind: "aiRefused"; message: string }
  | { kind: "tooLong" }
  | { kind: "aiUnavailable" }
  | { kind: "aborted" }
  | { kind: "failed" };

/** What came of adding a document. */
export type DocumentResult =
  | { kind: "attached"; document: ExtractedSourceDocument }
  | { kind: "wrongType" }
  | { kind: "tooLarge" }
  | { kind: "unreadable"; name: string }
  | { kind: "trialEnded" }
  | { kind: "unavailable" }
  | { kind: "failed" };

const PDF = "application/pdf";
const DOCX = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const XLSX = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const DOCUMENT_TYPES = new Set([PDF, DOCX, XLSX]);
const DOCUMENT_EXTENSIONS = [".pdf", ".docx", ".xlsx", ".txt", ".md"];

/** What the file picker offers: the kinds the server can read. */
export const DOCUMENT_ACCEPT = [...DOCUMENT_EXTENSIONS, PDF, DOCX, XLSX, "text/*"].join(",");

/** The server's upload limit (IDocumentTextExtractor.MaxFileBytes). */
export const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;

const statusOf = (error: unknown): number | null => {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === "number" ? status : null;
};

const isAbort = (error: unknown): boolean => (error as { name?: unknown } | null)?.name === "AbortError";

/** The server's sentence when a draft is too big for one reply ("…within the output limit…"). */
const mentionsOutputLimit = (error: unknown): boolean => error instanceof Error && /output limit/i.test(error.message);

/** A file the server can read, by its type or, when the browser gives none, its name. */
function isReadableDocument(file: Pick<File, "name" | "type">): boolean {
  const type = file.type.trim().toLowerCase();
  if (type.startsWith("text/") || DOCUMENT_TYPES.has(type)) return true;
  const name = file.name.toLowerCase();
  return DOCUMENT_EXTENSIONS.some((extension) => name.endsWith(extension));
}

/** "1 October": when the AI drafts reset, as the server's own sentence says it. */
export function resetDate(utc: string | null): string | null {
  if (!utc) return null;
  const date = new Date(utc);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString("en-GB", { day: "numeric", month: "long", timeZone: "UTC" });
}

/**
 * The AI meter from the plan's allowance. Nothing governs the organisation, or the plan sets no cap: unmetered. A cap of none:
 * the plan has no AI drafts. Otherwise how many are left this month, or that they're used up until the reset.
 */
export function aiMeter(allowance: OrganisationAllowance | null): AiMeter {
  if (!allowance?.governed || allowance.aiGenerations.allowed == null) return { state: "unmetered" };
  const { allowed } = allowance.aiGenerations;
  if (allowed <= 0) return { state: "locked" };
  const used = allowance.aiGenerations.used ?? 0;
  const resetsOn = resetDate(allowance.aiResetsAtUtc);
  return used >= allowed ? { state: "usedUp", allowed, resetsOn } : { state: "left", left: allowed - used, resetsOn };
}

/** The Describe it card's line under its body, and whether it can be chosen. */
export function describeCard(meter: AiMeter): { meta: string | null; disabled: boolean } {
  switch (meter.state) {
    case "unmetered":
      return { meta: copy.start.recommended, disabled: false };
    case "left":
      return { meta: `${copy.start.recommended} · ${fill(copy.start.describe.left, { n: meter.left })}`, disabled: false };
    case "locked":
      return { meta: copy.start.describe.locked, disabled: true };
    case "usedUp":
      return { meta: meter.resetsOn ? fill(copy.start.describe.resets, { date: meter.resetsOn }) : null, disabled: true };
  }
}

/**
 * Exactly what's sent to be drafted: the description, what the dial is for when the author said, the kind they chose, and the
 * text of a document when they added one.
 */
export function describeRequest(choices: DescribeChoices): GenerateDefinitionRequest {
  const goal = choices.purpose.trim();
  return {
    description: choices.description.trim(),
    kind: choices.kind,
    ...(goal ? { goal } : {}),
    ...(choices.document ? { sourceDocument: choices.document.text } : {}),
  };
}

/**
 * Reads a document the author picked. A file the server can't read, or one over its 10 MB limit, is turned away here without
 * being sent. Otherwise the server's answer: its text, a file it couldn't read, an ended trial, or documents switched off.
 */
export async function readDocument(deps: Pick<DescribeDeps, "extractDocument">, file: File): Promise<DocumentResult> {
  if (!isReadableDocument(file)) return { kind: "wrongType" };
  if (file.size > MAX_DOCUMENT_BYTES) return { kind: "tooLarge" };
  try {
    return { kind: "attached", document: await deps.extractDocument(file) };
  } catch (error) {
    switch (statusOf(error)) {
      case 400:
        return { kind: "unreadable", name: file.name };
      case 402:
        return { kind: "trialEnded" };
      case 404:
        return { kind: "unavailable" };
      default:
        return { kind: "failed" };
    }
  }
}

/** The sentence for a document that wasn't added, or null when there's nothing to say. */
export function documentProblem(result: DocumentResult): string | null {
  const words = copy.start.describe.document;
  switch (result.kind) {
    case "wrongType":
      return words.wrongType;
    case "tooLarge":
      return words.tooLarge;
    case "unreadable":
      return fill(words.unreadable, { name: result.name });
    case "unavailable":
      return words.unavailable;
    case "failed":
      return words.failed;
    case "attached":
    case "trialEnded":
      return null;
  }
}

/**
 * The draft the builder creates from what the AI returned. Group proposals are dropped: they describe child dials an author's
 * builder never makes. A name or purpose the author typed wins over the draft's own.
 */
export function draftFromGenerated(generated: GeneratedDefinition, choices: DescribeChoices): GeneratedDefinition {
  const draft = { ...generated };
  delete draft.groupChildProposals;
  delete draft.groupLinkProposals;
  const name = choices.name.trim();
  const purpose = choices.purpose.trim();
  return { ...draft, ...(name ? { name } : {}), ...(purpose ? { description: purpose } : {}) };
}

/**
 * Drafts a dial from a description and opens it. The plan is checked first, so a full plan or a month's drafts already used
 * costs nothing; the AI's refusal is shown in the server's words; and a draft the author stopped waiting for is never created.
 * A draft too big to make from the attached document says so, and AI drafts being unavailable here says that.
 */
export async function startFromDescription(deps: DescribeDeps, choices: DescribeChoices, signal: AbortSignal): Promise<DescribeResult> {
  const allowance = await deps.getAllowance();
  if (!allowance.canCreateDefinition) return { kind: "limit", allowance };

  const meter = aiMeter(allowance);
  if (meter.state === "locked") return { kind: "aiRefused", message: copy.start.describe.locked };
  if (meter.state === "usedUp") {
    return { kind: "aiRefused", message: fill(copy.limits.aiUsedUp, { n: meter.allowed, date: meter.resetsOn ?? "" }) };
  }

  let generated: GeneratedDefinition;
  try {
    generated = await deps.generate(describeRequest(choices), signal);
  } catch (error) {
    if (isAbort(error) || signal.aborted) return { kind: "aborted" };
    const status = statusOf(error);
    if (status === 402) return { kind: "trialEnded" };
    if (status === 400) return { kind: "aiRefused", message: error instanceof Error && error.message ? error.message : copy.start.describe.failed };
    if (status === 503) return { kind: "aiUnavailable" };
    if (status === 502 && choices.document && mentionsOutputLimit(error)) return { kind: "tooLong" };
    return { kind: "failed" };
  }
  if (signal.aborted) return { kind: "aborted" };

  return createDraft(deps, () => deps.createDefinition(draftFromGenerated(generated, choices)), choices.noun);
}
